import { Card, CardContent } from '@/components/ui/card';
import {
  Calculator,
  Activity,
  Ruler,
  TrendingUp,
  Droplets,
  Scale,
  Shield,
  Zap,
  BarChart3,
} from 'lucide-react';

const features = [
  {
    icon: Calculator,
    title: 'Accurate BMI Calculation',
    description: 'Precise BMI calculation with support for both metric and US imperial units. Instant results with WHO category classification.',
    color: 'primary'
  },
  {
    icon: Activity,
    title: 'Athlete Mode',
    description: 'Special performance bands designed for athletes. Understand how your BMI relates to endurance, power, and strength training.',
    color: 'accent'
  },
  {
    icon: Ruler,
    title: 'Waist-to-Height Ratio',
    description: 'Advanced health metric that better predicts health risks than BMI alone. Get a clearer picture of your body composition.',
    color: 'info'
  },
  {
    icon: TrendingUp,
    title: 'Running Pace Impact',
    description: 'Estimate how weight changes might affect your running performance. Perfect for endurance athletes and runners.',
    color: 'success'
  },
  {
    icon: Droplets,
    title: 'Hydration Calculator',
    description: 'Get personalized hydration recommendations based on your weight and exercise conditions.',
    color: 'info'
  },
  {
    icon: BarChart3,
    title: 'Progress Tracking',
    description: 'Track your BMI over time with beautiful charts and statistics. All data stored locally on your device.',
    color: 'accent'
  },
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'All calculations happen in your browser. No data is sent to any server. Your health data stays with you.',
    color: 'success'
  },
  {
    icon: Zap,
    title: 'Instant Results',
    description: 'Get comprehensive results instantly. No waiting, no loading. Just fast, accurate health metrics.',
    color: 'warning'
  },
];

const colorClasses = {
  primary: {
    bg: 'bg-primary/10',
    icon: 'text-primary',
  },
  accent: {
    bg: 'bg-accent/10',
    icon: 'text-accent',
  },
  info: {
    bg: 'bg-info/10',
    icon: 'text-info',
  },
  success: {
    bg: 'bg-success/10',
    icon: 'text-success',
  },
  warning: {
    bg: 'bg-warning/10',
    icon: 'text-warning',
  },
};

export const FeaturesSection = () => {
  return (
    <section id="features" className="py-16 sm:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Everything You Need for
            <span className="gradient-text"> Health Tracking</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            More than just a BMI calculator. Get comprehensive health metrics designed for both everyday users and serious athletes.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const colors = colorClasses[feature.color];
            
            return (
              <Card 
                key={index} 
                className="card-elevated group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Stats Row */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-4xl font-bold gradient-text">8+</p>
            <p className="text-sm text-muted-foreground mt-1">Health Metrics</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold gradient-text">2</p>
            <p className="text-sm text-muted-foreground mt-1">Unit Systems</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold gradient-text">100%</p>
            <p className="text-sm text-muted-foreground mt-1">Privacy</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold gradient-text">0</p>
            <p className="text-sm text-muted-foreground mt-1">Signup Required</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
